import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  GeneralSalesAdjustmentRow,
  GeneralSalesCancelledOrderRow,
  GeneralSalesCustomerMixRow,
  GeneralSalesCustomerType,
  GeneralSalesDiscountRow,
  GeneralSalesExtraRow,
  GeneralSalesKpi,
  GeneralSalesPackageRow,
  GeneralSalesMembershipPlanRow,
  GeneralSalesPrepaidBookRow,
  GeneralSalesReportData,
  GeneralSalesRevenueCategory,
  GeneralSalesRewashEmployeeRow,
  GeneralSalesRewashReasonRow,
} from '../../models/general-sales-report.model';
import { InfoTipComponent } from '../../../shared/components/info-tip';
import { GENERAL_SALES_TIPS } from '../../report-field-definitions';

interface AxisTick {
  value: number;
  label: string;
  y: number;
}

interface DateTick {
  label: string;
  x: number;
}

/** One hover target on a line chart: an invisible band plus what to show for it. */
interface HoverBand {
  x: number;
  width: number;
  cx: number;
  cy: number;
  label: string;
  lines: { text: string; color?: string }[];
}

interface FlowBar {
  label: string;
  newSignups: number;
  cancellations: number;
  x: number;
  newX: number;
  newY: number;
  newHeight: number;
  cancelX: number;
  cancelY: number;
  cancelHeight: number;
  labelX: number;
  /** Only some buckets get an x label: 37 weekly labels collide into a smear. */
  showLabel: boolean;
}

interface MixSegment {
  label: string;
  color: string;
  sharePct: number;
  revenue: number;
}

/** Series colours are validated for colour-vision separation; keep them in step with the design canvas. */
const SERIES_VIOLET = '#5147D8';
const SERIES_GREEN = '#1B8F5E';
const SERIES_AMBER = '#C2620A';
const SERIES_MUTED = '#A7ABB3';
const SERIES_TEAL = '#0E7C86';

/** Revenue breakdown categories (CWS-1368). Unlisted categories fall back to muted. */
const REVENUE_CATEGORY_COLORS: Record<GeneralSalesRevenueCategory, string> = {
  washes: SERIES_VIOLET,
  extras: SERIES_GREEN,
  memberships: SERIES_TEAL,
  prepaid_books: SERIES_AMBER,
  other: SERIES_MUTED,
  fees: SERIES_MUTED,
};

const SALES_PLOT = { left: 56, right: 1036, top: 20, bottom: 220 };
const MEMBER_PLOT = { left: 56, right: 760, top: 20, bottom: 180 };
// left has to clear the widest y tick label ('4,000') at the 10px axis size.
const FLOW_PLOT = { left: 52, right: 370, top: 20, bottom: 180 };
const VOLUME_PLOT = { left: 48, right: 500, top: 20, bottom: 160 };

@Component({
  selector: 'app-general-sales-report-results',
  standalone: true,
  imports: [CommonModule, InfoTipComponent],
  templateUrl: './general-sales-report-results.component.html',
  styleUrl: './general-sales-report-results.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GeneralSalesReportResultsComponent implements OnChanges {
  @Input({ required: true }) report!: GeneralSalesReportData;
  @Input() exportDisabled = false;
  @Input() exportingCsv = false;
  @Input() exportingXlsx = false;
  @Input() exportingPdf = false;
  /** Whether exports carry the cancelled-order list. Off by default: it can run to hundreds of rows. */
  @Input() includeCancelledOrdersInExport = false;
  @Output() includeCancelledOrdersInExportChange = new EventEmitter<boolean>();
  @Output() exportCsv = new EventEmitter<void>();
  @Output() exportXlsx = new EventEmitter<void>();
  @Output() exportPdf = new EventEmitter<void>();

  readonly seriesViolet = SERIES_VIOLET;
  readonly seriesGreen = SERIES_GREEN;
  readonly seriesAmber = SERIES_AMBER;
  readonly seriesMuted = SERIES_MUTED;
  readonly seriesTeal = SERIES_TEAL;
  readonly tips = GENERAL_SALES_TIPS;

  grossPoints = '';
  netPoints = '';
  netArea = '';
  previousPoints = '';
  salesYTicks: AxisTick[] = [];
  salesXTicks: DateTick[] = [];

  carsPoints = '';
  carsMarkers: { x: number; y: number }[] = [];
  carsYTicks: AxisTick[] = [];
  carsXTicks: DateTick[] = [];
  ticketPoints = '';
  ticketMarkers: { x: number; y: number }[] = [];
  ticketYTicks: AxisTick[] = [];
  ticketXTicks: DateTick[] = [];

  memberPoints = '';
  memberBands: HoverBand[] = [];
  memberYTicks: AxisTick[] = [];
  memberXTicks: DateTick[] = [];
  memberLastX = MEMBER_PLOT.right;
  memberLastY = MEMBER_PLOT.bottom;

  flowBars: FlowBar[] = [];
  /** Bar width is computed from how many buckets there are, never assumed. */
  flowBarWidth = 22;
  flowYTicks: AxisTick[] = [];

  salesBands: HoverBand[] = [];
  carsBands: HoverBand[] = [];
  ticketBands: HoverBand[] = [];
  hoveredChart: 'sales' | 'cars' | 'ticket' | 'members' | null = null;
  hoveredIndex = -1;

  revenueSegments: MixSegment[] = [];
  netSharePct = 0;
  deductionSharePct = 0;

  ngOnChanges(): void {
    this.buildSalesTrend();
    this.buildVolumeTrend();
    this.buildTicketTrend();
    this.buildMembershipTrend();
    this.buildMembershipFlow();
    this.buildRevenueSegments();
  }

  trackByKpi(_index: number, kpi: GeneralSalesKpi): string {
    return kpi.key;
  }

  trackByPackage(_index: number, row: GeneralSalesPackageRow): string {
    return row.packageId;
  }

  trackByExtra(_index: number, row: GeneralSalesExtraRow): string {
    return row.addonId;
  }

  trackByCustomerType(_index: number, row: GeneralSalesCustomerMixRow): string {
    return row.type;
  }

  trackByDiscount(_index: number, row: GeneralSalesDiscountRow): string {
    return row.type;
  }

  trackByRewashReason(_index: number, row: GeneralSalesRewashReasonRow): string {
    // The slug is the stable identity; two custom reasons could share a label.
    return row.reasonKey ?? row.reason;
  }

  trackByRewashEmployee(_index: number, row: GeneralSalesRewashEmployeeRow): string {
    return row.employeeId;
  }

  trackByAdjustment(_index: number, row: GeneralSalesAdjustmentRow): string {
    return row.category;
  }

  trackByCancelledOrder(_index: number, row: GeneralSalesCancelledOrderRow): string {
    return row.orderId;
  }

  trackByPrepaidBook(_index: number, row: GeneralSalesPrepaidBookRow): string {
    return row.bookId;
  }

  /** A deleted package groups as a single 'Unknown plan' row with a null id. */
  trackByPlanRow(_index: number, row: GeneralSalesMembershipPlanRow): string {
    return row.packageId ?? `name:${row.planName}`;
  }

  /** Ending members by plan; absent on a report served before CWS-1426. */
  get planRows(): GeneralSalesMembershipPlanRow[] {
    return this.report?.membership?.byPlan ?? [];
  }

  /**
   * Summed from the rows rather than read from base.ending, so the Total cell
   * reports what the table actually shows and a mismatch becomes visible.
   */
  get planRowsTotal(): number {
    return this.planRows.reduce((sum, row) => sum + row.activeMembers, 0);
  }

  /**
   * True when one customer holds plans on more than one row. The Customers
   * column counts distinct customers WITHIN a plan, so a customer on two plans
   * is in two rows while the Total counts them once — the column legitimately
   * adds to more than its own total, and that needs saying rather than looking
   * like the arithmetic is broken.
   */
  get planRowsCustomersOverlap(): boolean {
    const summed = this.planRows.reduce((sum, row) => sum + row.activeCustomers, 0);
    return summed > (this.report?.membership?.base?.endingCustomers ?? 0);
  }

  /**
   * What a cancelled order was for. Falls back to the wash type when no package
   * was on the order — a prepaid or standalone extra has none.
   */
  cancelledOrderItem(row: GeneralSalesCancelledOrderRow): string {
    return row.packageName || row.washType;
  }

  /** Refund state reads as a dash when no money was taken, not as blank. */
  cancelledOrderRefund(row: GeneralSalesCancelledOrderRow): string {
    if (!row.refundStatus) {
      return 'No refund';
    }
    const spaced = row.refundStatus.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  /** Share of a dimension for one customer type, for the paired bars. */
  mixShare(type: GeneralSalesCustomerType, dimension: 'washes' | 'revenue'): number {
    const row = this.report?.customerMix?.find(entry => entry.type === type);
    if (!row) {
      return 0;
    }
    return dimension === 'washes' ? row.washSharePct : row.revenueSharePct;
  }

  /** Plain-English reading of the mix, so the section answers §10 rather than only stating it. */
  get dependenceLine(): string | null {
    const summary = this.report?.customerMixSummary;
    if (!summary) {
      return null;
    }
    const wash = summary.membershipWashSharePct;
    const revenue = summary.membershipRevenueSharePct;
    if (wash === 0) {
      return 'No member washes in this period — every wash was retail.';
    }
    const relation = summary.membershipWorthLessPerWash
      ? `${wash}% of washes but ${revenue}% of revenue — each member wash is worth less than a retail one`
      : `${wash}% of washes and ${revenue}% of revenue`;
    return `Membership brings ${relation}.`;
  }

  get dependenceTrend(): string | null {
    const delta = this.report?.customerMixSummary?.washShareDeltaPts;
    if (delta === null || delta === undefined) {
      return null;
    }
    if (Math.abs(delta) < 0.05) {
      return 'Unchanged vs the previous period.';
    }
    const direction = delta > 0 ? 'up' : 'down';
    return `${direction} ${Math.abs(delta)} points vs the previous period.`;
  }

  /** Colour of a KPI delta: a rise in churn or leakage is not good news. */
  deltaIsPositive(kpi: GeneralSalesKpi): boolean {
    if (kpi.deltaPct === null) {
      return true;
    }
    return kpi.higherIsBetter ? kpi.deltaPct >= 0 : kpi.deltaPct < 0;
  }

  absDelta(value: number): number {
    return Math.abs(value);
  }

  /**
   * Prepaid is a real classification, not a gap, so it gets its own colour
   * rather than the muted one that marks missing data.
   */
  packageColor(index: number, unmapped: boolean, prepaid = false): string {
    if (unmapped) {
      return SERIES_MUTED;
    }
    if (prepaid) {
      return SERIES_TEAL;
    }
    const palette = [SERIES_VIOLET, SERIES_GREEN, SERIES_AMBER];
    return palette[index % palette.length];
  }

  /** Prepaid shares the teal it has in the package mix, so one colour means one thing. */
  customerColor(type: string): string {
    if (type === 'membership') {
      return SERIES_GREEN;
    }
    if (type === 'prepaid') {
      return SERIES_TEAL;
    }
    return SERIES_VIOLET;
  }

  /**
   * Chart inputs are defensive on purpose.
   *
   * A series can arrive without a field an older API build never sent, and
   * `Math.max(...[undefined])` is NaN — which renders as a literal "NaN" axis.
   * A chart must degrade to "not drawn", never to a broken drawing.
   */
  private finite(value: unknown): number | null {
    // Number(null) is 0 and Number('') is 0, so a missing value would otherwise
    // be plotted as a real zero — a false dip to the baseline rather than a gap.
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private finiteValues(values: unknown[]): number[] {
    return values.map(v => this.finite(v)).filter((v): v is number => v !== null);
  }

  /** Highest value across every series drawn on one axis. */
  private axisMaxFor(values: unknown[], floor = 1): number | null {
    const numbers = this.finiteValues(values);
    if (numbers.length === 0) {
      return null;
    }
    return this.niceCeiling(Math.max(...numbers, floor));
  }

  onChartHover(chart: 'sales' | 'cars' | 'ticket' | 'members', index: number): void {
    this.hoveredChart = chart;
    this.hoveredIndex = index;
  }

  onChartLeave(): void {
    this.hoveredChart = null;
    this.hoveredIndex = -1;
  }

  activeBand(chart: 'sales' | 'cars' | 'ticket' | 'members'): HoverBand | null {
    if (this.hoveredChart !== chart || this.hoveredIndex < 0) {
      return null;
    }
    const bands =
      chart === 'sales'
        ? this.salesBands
        : chart === 'cars'
          ? this.carsBands
          : chart === 'ticket'
            ? this.ticketBands
            : this.memberBands;
    return bands[this.hoveredIndex] ?? null;
  }

  /** Keep the tooltip inside the drawing rather than letting it run off an edge. */
  tooltipX(band: HoverBand, width: number, plotRight: number, plotLeft: number): number {
    const half = width / 2;
    return Math.min(Math.max(band.cx - half, plotLeft), plotRight - width);
  }

  /** Evenly spaced hover targets, one per plotted point. */
  private buildBands(
    plot: { left: number; right: number },
    count: number,
    describe: (index: number) => {
      cy: number;
      label: string;
      lines: { text: string; color?: string }[];
    }
  ): HoverBand[] {
    if (count === 0) {
      return [];
    }
    const span = plot.right - plot.left;
    const width = count > 1 ? span / (count - 1) : span;
    const toX = this.scaleX(plot, count);
    return Array.from({ length: count }, (_, index) => {
      const described = describe(index);
      return {
        x: this.round1(Math.max(plot.left, toX(index) - width / 2)),
        width: this.round1(width),
        cx: toX(index),
        cy: described.cy,
        label: described.label,
        lines: described.lines,
      };
    });
  }

  private money(value: number | null): string {
    if (value === null) {
      return 'N/A';
    }
    return `$${Math.round(value).toLocaleString('en-US')}`;
  }

  private money2(value: number | null): string {
    if (value === null) {
      return 'N/A';
    }
    return `$${value.toFixed(2)}`;
  }

  /** Label for the trend axis, so a weekly bucket is never read as a day. */
  get trendBucketLabel(): string {
    const granularity = this.report?.volume?.trendGranularity ?? 'day';
    return granularity === 'day' ? 'Daily' : granularity === 'week' ? 'Weekly' : 'Monthly';
  }

  /** A KPI shows its absolute change when a percentage is undefined (PRD §6, §20). */
  hasComparison(kpi: GeneralSalesKpi): boolean {
    return kpi.deltaPct !== null || (kpi.deltaAbs !== null && kpi.deltaAbs !== undefined);
  }

  deltaLabel(kpi: GeneralSalesKpi): string {
    if (kpi.deltaPct !== null) {
      return `${Math.abs(kpi.deltaPct)}% vs previous`;
    }
    const abs = kpi.deltaAbs ?? 0;
    const sign = abs >= 0 ? '+' : '−';
    return `${sign}${Math.abs(abs).toLocaleString('en-US')} vs previous`;
  }

  deltaIsUp(kpi: GeneralSalesKpi): boolean {
    const value = kpi.deltaPct !== null ? kpi.deltaPct : (kpi.deltaAbs ?? 0);
    return value >= 0;
  }

  /**
   * Mark individual points on a sparse series.
   *
   * Average ticket omits buckets with no washes, so two surviving points get
   * joined by a long straight line that reads as a trend through days that were
   * never measured. Markers make them read as the two readings they are.
   */
  private markersFor(points: { x: number; y: number }[]): { x: number; y: number }[] {
    return points.length > 0 && points.length <= 12 ? points : [];
  }

  /** PRD §19 Wash Volume Trend: cars per day across the selected window. */
  private buildVolumeTrend(): void {
    const points = this.report?.volume?.trend ?? [];
    if (points.length === 0) {
      this.carsPoints = '';
      this.carsYTicks = [];
      this.carsXTicks = [];
      this.carsBands = [];
      return;
    }
    const axisMax = this.axisMaxFor(points.map(p => p.carsPerDay));
    if (axisMax === null) {
      this.carsPoints = '';
      this.carsMarkers = [];
      this.carsYTicks = [];
      this.carsXTicks = [];
      this.carsBands = [];
      return;
    }
    const toX = this.scaleX(VOLUME_PLOT, points.length);
    const toY = this.scaleY(VOLUME_PLOT, axisMax);
    const carsDrawn = points
      .map((p, i) => ({ i, value: this.finite(p.carsPerDay) }))
      .filter(e => e.value !== null);
    this.carsPoints = carsDrawn.map(e => `${toX(e.i)},${toY(e.value as number)}`).join(' ');
    this.carsMarkers = this.markersFor(
      carsDrawn.map(e => ({ x: toX(e.i), y: toY(e.value as number) }))
    );
    this.carsYTicks = this.buildValueTicks(axisMax, toY, v =>
      Math.round(v).toLocaleString('en-US')
    );
    this.carsXTicks = this.buildDateTicks(
      points.map(p => p.date),
      toX
    );
    this.carsBands = this.buildBands(VOLUME_PLOT, points.length, index => {
      const point = points[index];
      const perDay = this.finite(point.carsPerDay) ?? 0;
      return {
        cy: toY(perDay),
        label: this.fullDate(point.date),
        lines: [
          { text: `${perDay.toLocaleString('en-US')} cars/day`, color: SERIES_GREEN },
          { text: `${(this.finite(point.cars) ?? 0).toLocaleString('en-US')} in bucket` },
        ],
      };
    });
  }

  /** PRD §19 Average Ticket Trend. Buckets with no washes are skipped, not drawn as zero. */
  private buildTicketTrend(): void {
    const points = (this.report?.volume?.trend ?? []).filter(
      p => this.finite(p.averageTicket) !== null
    );
    const axisMax = this.axisMaxFor(points.map(p => p.averageTicket));
    if (points.length === 0 || axisMax === null) {
      this.ticketPoints = '';
      this.ticketMarkers = [];
      this.ticketYTicks = [];
      this.ticketXTicks = [];
      this.ticketBands = [];
      return;
    }
    const toX = this.scaleX(VOLUME_PLOT, points.length);
    const toY = this.scaleY(VOLUME_PLOT, axisMax);
    const ticketDrawn = points.map((p, i) => ({
      x: toX(i),
      y: toY(this.finite(p.averageTicket) as number),
    }));
    this.ticketPoints = ticketDrawn.map(e => `${e.x},${e.y}`).join(' ');
    this.ticketMarkers = this.markersFor(ticketDrawn);
    this.ticketYTicks = this.buildValueTicks(axisMax, toY, v => `$${Math.round(v)}`);
    this.ticketXTicks = this.buildDateTicks(
      points.map(p => p.date),
      toX
    );
    this.ticketBands = this.buildBands(VOLUME_PLOT, points.length, index => {
      const point = points[index];
      return {
        cy: toY(this.finite(point.averageTicket) as number),
        label: this.fullDate(point.date),
        lines: [
          { text: `${this.money2(this.finite(point.averageTicket))} average`, color: SERIES_AMBER },
          { text: `${(this.finite(point.cars) ?? 0).toLocaleString('en-US')} washes` },
        ],
      };
    });
  }

  private buildSalesTrend(): void {
    const points = this.report?.volume?.trend ?? [];
    if (points.length === 0) {
      this.grossPoints = '';
      this.netPoints = '';
      this.netArea = '';
      this.previousPoints = '';
      this.salesYTicks = [];
      this.salesXTicks = [];
      this.salesBands = [];
      return;
    }

    // The comparison series shares this axis. Scaling to the current period alone
    // sent a larger previous period off the top of the chart and, with the SVG
    // not clipping, straight out over the rest of the page.
    const axisMax = this.axisMaxFor([
      ...points.map(point => point.gross),
      ...points.map(point => point.net),
      ...points.map(point => point.previousGross),
    ]);
    if (axisMax === null) {
      this.grossPoints = '';
      this.netPoints = '';
      this.netArea = '';
      this.previousPoints = '';
      this.salesYTicks = [];
      this.salesXTicks = [];
      this.salesBands = [];
      return;
    }
    const toX = this.scaleX(SALES_PLOT, points.length);
    const toY = this.scaleY(SALES_PLOT, axisMax);

    this.grossPoints = points.map((point, i) => `${toX(i)},${toY(point.gross)}`).join(' ');
    this.netPoints = points.map((point, i) => `${toX(i)},${toY(point.net)}`).join(' ');
    this.netArea = this.buildArea(
      points.map((point, i) => `${toX(i)},${toY(point.net)}`),
      toX(0),
      toX(points.length - 1),
      SALES_PLOT.bottom
    );

    const comparison = points
      .map((point, i) => ({ i, value: this.finite(point.previousGross) }))
      .filter(entry => entry.value !== null);
    this.previousPoints =
      comparison.length > 1
        ? comparison.map(entry => `${toX(entry.i)},${toY(entry.value as number)}`).join(' ')
        : '';

    this.salesYTicks = this.buildValueTicks(axisMax, toY, value => this.compactCurrency(value));
    this.salesXTicks = this.buildDateTicks(
      points.map(point => point.date),
      toX
    );
    this.salesBands = this.buildBands(SALES_PLOT, points.length, index => {
      const point = points[index];
      const previous = this.finite(point.previousGross);
      return {
        cy: toY(point.gross),
        label: this.fullDate(point.date),
        lines: [
          { text: `Gross ${this.money(point.gross)}`, color: SERIES_VIOLET },
          { text: `Net ${this.money(point.net)}`, color: SERIES_GREEN },
          ...(previous === null
            ? []
            : [{ text: `Previous ${this.money(previous)}`, color: SERIES_MUTED }]),
        ],
      };
    });
  }

  private buildMembershipTrend(): void {
    const points = this.report?.membership?.trend ?? [];
    if (points.length === 0) {
      this.memberPoints = '';
      this.memberYTicks = [];
      this.memberXTicks = [];
      this.memberBands = [];
      return;
    }

    const values = points.map(point => point.activeMembers);
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    // Members move in small steps against a large base; a zero baseline would flatten the line,
    // so the axis is banded around the data and the floor is labelled in the chart header.
    const axisMin = Math.floor((rawMin - (rawMax - rawMin) * 0.5) / 100) * 100;
    const axisMax = Math.ceil((rawMax + (rawMax - rawMin) * 0.3) / 100) * 100;
    const span = Math.max(1, axisMax - axisMin);

    const toX = this.scaleX(MEMBER_PLOT, points.length);
    const toY = (value: number): number =>
      this.round1(
        MEMBER_PLOT.bottom - ((value - axisMin) / span) * (MEMBER_PLOT.bottom - MEMBER_PLOT.top)
      );

    this.memberPoints = points.map((point, i) => `${toX(i)},${toY(point.activeMembers)}`).join(' ');
    this.memberLastX = toX(points.length - 1);
    this.memberLastY = toY(values[values.length - 1]);

    const steps = 3;
    const ticks: AxisTick[] = [];
    for (let i = 0; i <= steps; i += 1) {
      const value = axisMin + (span / steps) * i;
      ticks.push({ value, label: Math.round(value).toLocaleString('en-US'), y: toY(value) });
    }
    this.memberYTicks = ticks;
    this.memberXTicks = this.buildDateTicks(
      points.map(point => point.date),
      toX
    );
    this.memberBands = this.buildBands(MEMBER_PLOT, points.length, index => {
      const point = points[index];
      return {
        cy: toY(point.activeMembers),
        label: this.fullDate(point.date),
        lines: [
          {
            text: `${point.activeMembers.toLocaleString('en-US')} active members`,
            color: SERIES_VIOLET,
          },
        ],
      };
    });
  }

  private buildMembershipFlow(): void {
    const flow = this.report?.membership?.flow ?? [];
    if (flow.length === 0) {
      this.flowBars = [];
      this.flowYTicks = [];
      return;
    }

    const axisMax = Math.max(
      10,
      this.axisMaxFor(flow.flatMap(point => [point.newSignups, point.cancellations])) ?? 10
    );
    const toY = this.scaleY(FLOW_PLOT, axisMax);
    const groupWidth = (FLOW_PLOT.right - FLOW_PLOT.left) / flow.length;
    // Two bars and a 1px gap have to fit inside one bucket. A fixed 22px width
    // made 37 weekly buckets overlap into a single solid block.
    const gap = groupWidth > 12 ? 2 : 1;
    const barWidth = Math.max(1.5, Math.min(22, (groupWidth - gap) / 2 - 1));
    this.flowBarWidth = this.round1(barWidth);
    // Show at most 8 labels, evenly spaced, always including the first and last.
    // Stepping and then forcing the last one on top of it overshoots the cap.
    const wanted = Math.min(8, flow.length);
    const labelled = new Set<number>();
    for (let i = 0; i < wanted; i += 1) {
      labelled.add(Math.round(((flow.length - 1) * i) / Math.max(1, wanted - 1)));
    }

    this.flowBars = flow.map((point, index) => {
      const groupX = FLOW_PLOT.left + groupWidth * index + (groupWidth - barWidth * 2 - gap) / 2;
      const newY = toY(point.newSignups);
      const cancelY = toY(point.cancellations);
      return {
        label: point.label,
        newSignups: point.newSignups,
        cancellations: point.cancellations,
        x: groupX,
        newX: this.round1(groupX),
        newY,
        newHeight: this.round1(FLOW_PLOT.bottom - newY),
        cancelX: this.round1(groupX + barWidth + gap),
        cancelY,
        cancelHeight: this.round1(FLOW_PLOT.bottom - cancelY),
        labelX: this.round1(groupX + barWidth + gap / 2),
        showLabel: labelled.has(index),
      };
    });

    this.flowYTicks = this.buildValueTicks(axisMax, toY, value =>
      Math.round(value).toLocaleString('en-US')
    );
  }

  /** One colour per revenue category, shared by the stack, legend and table. */
  categoryColor(category: GeneralSalesRevenueCategory): string {
    return REVENUE_CATEGORY_COLORS[category] ?? SERIES_MUTED;
  }

  private buildRevenueSegments(): void {
    const breakdown = this.report?.volume?.revenueBreakdown ?? [];
    this.revenueSegments = breakdown.map(row => ({
      label: row.label,
      color: this.categoryColor(row.category),
      sharePct: row.sharePct,
      revenue: row.revenue,
    }));

    const gross = this.report?.volume?.grossSales ?? 0;
    const net = this.report?.volume?.netSales ?? 0;
    this.netSharePct = gross > 0 ? (net / gross) * 100 : 0;
    this.deductionSharePct = Math.max(0, 100 - this.netSharePct);
  }

  private buildArea(points: string[], firstX: number, lastX: number, baseline: number): string {
    if (points.length === 0) {
      return '';
    }
    const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point}`).join(' ');
    return `${line} L ${lastX},${baseline} L ${firstX},${baseline} Z`;
  }

  private buildValueTicks(
    axisMax: number,
    toY: (value: number) => number,
    format: (value: number) => string
  ): AxisTick[] {
    const steps = 4;
    const ticks: AxisTick[] = [];
    for (let i = 0; i <= steps; i += 1) {
      const value = (axisMax / steps) * i;
      ticks.push({ value, label: format(value), y: toY(value) });
    }
    return ticks;
  }

  private buildDateTicks(dates: string[], toX: (index: number) => number): DateTick[] {
    if (dates.length === 0) {
      return [];
    }
    const desired = Math.min(5, dates.length);
    const indexes = new Set<number>();
    for (let i = 0; i < desired; i += 1) {
      indexes.add(Math.round(((dates.length - 1) * i) / Math.max(1, desired - 1)));
    }
    return [...indexes].map(index => ({
      label: this.shortDate(dates[index]),
      x: toX(index),
    }));
  }

  private scaleX(plot: { left: number; right: number }, count: number): (index: number) => number {
    const span = Math.max(1, count - 1);
    return (index: number) => this.round1(plot.left + (index * (plot.right - plot.left)) / span);
  }

  private scaleY(
    plot: { top: number; bottom: number },
    axisMax: number
  ): (value: number) => number {
    const safeMax = axisMax > 0 ? axisMax : 1;
    return (value: number) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return plot.bottom;
      }
      // Never plot outside the drawing area, whatever the data says.
      const ratio = Math.min(1, Math.max(0, parsed / safeMax));
      return this.round1(plot.bottom - ratio * (plot.bottom - plot.top));
    };
  }

  private niceCeiling(value: number): number {
    if (value <= 0) {
      return 1;
    }
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    return Math.ceil(value / (magnitude / 2)) * (magnitude / 2);
  }

  private compactCurrency(value: number): string {
    if (value >= 1000) {
      const thousands = value / 1000;
      const rounded = Number.isInteger(thousands) ? thousands : Math.round(thousands * 10) / 10;
      return `$${rounded}k`;
    }
    return `$${Math.round(value)}`;
  }

  private fullDate(value: string): string {
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    const prefix = this.report?.volume?.trendGranularity === 'day' ? '' : 'week of ';
    return prefix + parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private shortDate(value: string): string {
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private round1(value: number): number {
    return Math.round(value * 10) / 10;
  }
}
